import { useEffect } from 'react';
import { resolvePageBackgroundMode } from '../utils/coverTint';

export const PAGE_BG_MODE_ATTR = 'data-shop-bg-mode';

/**
 * Publishes the owner's page-background choice as an attribute on
 * `document.documentElement`, and hands the resolved mode back.
 *
 * It goes on the ROOT ELEMENT for the same reason the tint tokens do: the
 * search drawer, the filter/sort sheets and the side menu render through a
 * PORTAL on `document.body`, outside the shop page's own element. A mode
 * scoped to the page wrapper would leave exactly those surfaces on the other
 * mode's palette — the "side menu doesn't match" bug, one setting along.
 *
 * The attribute is what the CSS FALLBACK palette keys off: it is the answer
 * for the moments and pages where no derived tint is published — before the
 * salon payload arrives, when the cover failed to load, when the canvas is
 * tainted, and on the all-collections grid, which has no cover at all.
 * `useTintVars` still wins over it when a tint does resolve, because an
 * inline style on the same element beats any selector.
 *
 * ── EVERY MODE IS WRITTEN, FULL INCLUDED ────────────────────────────────
 *
 * FULL used to REMOVE the attribute instead of writing `full`, on the reason
 * that FULL is today's page and its palette is the one already declared on
 * bare `:root` — writing an attribute for it would be a second place for
 * "today" to be defined.
 *
 * The palette half of that is right and is kept: there is still no
 * `[data-shop-bg-mode='full']` block anywhere, and `pageBackgroundModes`
 * asserts its absence. The DOM half was not. Deleting the attribute makes
 * "this shop chose FULL" and "there is no shop on this page" the same
 * document, so nothing downstream — CSS, a guard, a person opening the
 * inspector because the owner says the tabs are grey — can tell the mode from
 * the absence of one. Measured on the live shop: `shopBackgroundMode` was
 * FULL in the database and no mode of any kind was readable on the page.
 *
 * So the attribute is the STATE and the stylesheet blocks are the OVERRIDES.
 * FULL writes `full` and overrides nothing, which is exactly what FULL means.
 */
export default function usePageBackgroundMode(mode) {
  const resolved = resolvePageBackgroundMode(mode);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const root = document.documentElement;
    root.setAttribute(PAGE_BG_MODE_ATTR, resolved.toLowerCase());
    // Removed on unmount, exactly like the tint tokens — a shop's choice must
    // not follow the shopper onto the rest of the storefront.
    return () => root.removeAttribute(PAGE_BG_MODE_ATTR);
  }, [resolved]);

  return resolved;
}
